/**
 * These are layout components that are namedspaced under "Layout" to show that
 * they're all part of the same family, and should be used together (as opposed
 * to mixing and matching them with legacy or non-DS components).
 *
 * We're avoiding using "Root" as a component name for now in case we want to
 * use it for a page-level layout in the future.
 */
import { Area, Areas } from "./Area"
import { Block, Blocks } from "./Block"
import { Card, Cards } from "./Card"
import { Page, PageSpacingRow, PageTitle } from "./Page"

/**
 * This is the Layout component family, designed for high-level page organization.
 */
export const Layout = {
  /**
   * - Used to divide a page into sections.
   * - Collapsed state can be controlled or uncontrolled, because we're using Chakra's `useControllableState` hook.
   * - If you just use the `showCollapseButton` prop, the collapsed state will be uncontrolled.
   * - For controlled state, pass in `isCollapsed` and `onCollapsedChange`.
   * @example
   * const [isCollapsed, setIsCollapsed] = React.useState(false)
   * ...
   * <Area
   *   isCollapsed={isCollapsed}
   *   onCollapsedChange={() => setIsCollapsed(!isCollapsed)}
   *   showCollapseButton
   * >
   *   <div>Content</div>
   * </Area>
   */
  Area,
  /**
   * - Used to group multiple `Area` components together.
   */
  Areas,
  /**
   * - Should be used as the direct child of an `Area` (unless it's wrapped in a `Blocks` component).
   * - It's different to a `Card` in that it has styles that match higher levels of
   *   page hierarchy (bigger border-radius, more padding).
   */
  Block,
  /**
   * - Used to group multiple `Block` components together.
   */
  Blocks,
  /**
   * - Different to a `Block` in that it has styles that match lower levels of
   *   page hierarchy (smaller border-radius, less padding).
   */
  Card,
  /**
   * - Used to group multiple `Card` components together.
   */
  Cards,
  /**
   * - Used for pages of content (which do not include things like navs or top bars).
   * - It does not set page background color – this should be set on the `<body>` instead.
   */
  Page,
  /**
   * - Holds a content slot on each side of the page, separated by a `space-between` flex container.
   *   Usually used for the {@link PageTitle} and optional controls.
   * - The `startSlot` can be a {@link PageTitle} or something else, like a `Breadcrumb`.
   * - The `endSlot` can be a `React.Fragment` (`<></>`) with multiple items; it will handle the gap between them.
   * - You can stack multiple `PageSpacingRow` components on top of each other if you need
   *   (e.g. one with a breadcrumb + search input, then one with a title + controls).
   */
  PageSpacingRow,
  /**
   * - This provides an `<h1>` of the page title text with the correct styling.
   */
  PageTitle,
}
